import { useState, useCallback, useMemo } from "react";
import { ButtonComp, CardComp, PageTab, Loading } from "../../components";
import Payment from "../../pages/Admin/MemberRegistration/modules/Payment";
import PersonalDetails from "../../pages/Admin/MemberRegistration/modules/PersonalDetails";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { isValidPhoneFormat } from "../../utils/phoneUtils";
import {
  FormikCheckBtn,
  personal_details,
} from "../../pages/Admin/MemberRegistration/modules/FormikCheckBtn";
import FormLoadingOverlay from "../Common/FormLoadingOverlay";
import { PublicSignupStepper } from "../PublicSignup/PublicSignupShell";
import { toast } from "react-hot-toast";

export default function MemberRegistrationForm({
  branchId,
  memberId,
  isGeneratingId,
  regAmount = {},
  isLoadingFee,
  onSubmit,
  refetchMemberId,
  initialImgFile = null,
  initialErrorImg = "Click to upload image",
  formTitle = "Member Registration",
  loadingComponent = null,
  defaultPage = "personal_details",
  submitBtnText = "Proceed",
  showStepper = true,
  showHeader = true,
  isSubmitting = false,
  generationError = null,
  isPublicSignup = false,
  branches = [],
  companyDetails = null,
  isLoadingBranches = false,
  alignActionsEnd = false,
  compactPublicLayout = false,
  hideOrgSummaryOnDesktop = false,
  ...rest
}) {
  const { total: amount = 0, break_down = [] } = regAmount;
  const [pageName, setPageName] = useState(defaultPage);
  const [imgFile, setImgFile] = useState(initialImgFile);
  const [errorImg, setErrorImg] = useState(initialErrorImg);
  const [selectedBreakdown, setSelectedBreakdown] = useState([]);
  const [selectedBreakdownWithAmounts, setSelectedBreakdownWithAmounts] =
    useState([]);
  const [selectedAmount, setSelectedAmount] = useState(amount);
  const [memberNoAvailability, setMemberNoAvailability] = useState({
    isChecking: false,
    isAvailable: null,
    message: "",
  });

  const handleSelectionChange = useCallback(
    (ids, total, breakdownWithAmounts) => {
      setSelectedBreakdown(ids);
      setSelectedAmount(total);
      setSelectedBreakdownWithAmounts(breakdownWithAmounts || []);
    },
    []
  );

  const handleMemberNoAvailabilityChange = useCallback((status) => {
    setMemberNoAvailability(status);
  }, []);

  const updateImgFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImgFile(null);
      setErrorImg("Click to upload image");
      return;
    }
    const size = file?.size / 1024 / 1024;
    const type = file?.type?.split("/")[1];
    const validTypes = ["jpeg", "jpg", "png"];
    if (size > 10 || !validTypes.includes(type)) {
      setErrorImg(
        "Invalid. File size must not be more than 10mb\nFile must me either jpg jpeg png"
      );
      setImgFile(null);
    } else {
      setErrorImg(null);
      setImgFile(file);
    }
  };

  const formikValues = {
    ...personal_details,
    member_no: memberId,
    other_name: "",
    payment_method: "",
    branch_id: isPublicSignup ? "" : branchId || "",
  };
  const validationSchema = useMemo(
    () =>
      Yup.object({
        first_name: Yup.string().required("Member first name is required"),
        last_name: Yup.string().required("Member last name is required"),
        occupation_id: Yup.string()
          .required("Member Occupation is required")
          .test("not-empty", "Please select an occupation", (value) => {
            return value && value.toString().trim().length > 0 && value !== "";
          }),
        occupation: Yup.string().when("occupation_id", {
          is: (val) => val === "others",
          then: (schema) =>
            schema
              .required("Please specify your occupation")
              .test("not-empty", "Please specify your occupation", (value) => {
                return value && value.trim().length > 0;
              }),
          otherwise: (schema) => schema.notRequired(),
        }),
        dob: Yup.string().required("Input date of birth"),
        member_no: isPublicSignup
          ? Yup.string().notRequired()
          : Yup.string().required("Input member number"),
        address: Yup.string().required("Input valid address"),
        marital_id: Yup.string().required("Select marital status"),
        next_of_kin_name: Yup.string().required("Input next of kin name"),
        next_of_kin_phone: Yup.string()
          .required("Next of kin phone number is required")
          .test(
            "is-valid-phone",
            "Please enter a valid phone number",
            function (value) {
              if (!value) return false;
              return isValidPhoneFormat(value, "234");
            }
          ),
        relationship_status_id: Yup.string().required("Required"),
        gender_id: Yup.string().required("Required"),
        payment_method: Yup.string().required("Select payment method"),
        title_id: Yup.string().required("Select title"),
        branch_id:
          isPublicSignup && branches.length > 0
            ? Yup.string().required("Please select a branch")
            : Yup.string().notRequired(),
        phone: Yup.string()
          .required("Phone number is required")
          .test(
            "is-valid-phone",
            "Please enter a valid phone number",
            function (value) {
              if (!value) return false;
              return isValidPhoneFormat(value, "234");
            }
          ),
        email: Yup.string()
          .email("Input valid email address")
          .nullable()
          .notRequired(),
      }),
    [isPublicSignup, branches.length]
  );

  // Block while checking, when the number is taken, or when nothing is entered.
  // If the check itself could not run (a connection problem) the admin may continue:
  // the server checks the number again when the member is created.
  const memberNoBlocksProceed =
    !isPublicSignup &&
    (memberNoAvailability.isChecking ||
      !["available", "current", "unverified"].includes(memberNoAvailability.status));

  const usePage = useMemo(
    () => [
      {
        name: "personal_details",
        component: (
          <PersonalDetails
            updateImgFile={updateImgFile}
            imgFile={imgFile}
            errorImg={errorImg}
            setImgFile={setImgFile}
            setErrorImg={setErrorImg}
            generationError={generationError}
            isPublicSignup={isPublicSignup}
            branches={branches}
            companyDetails={companyDetails}
            isLoadingBranches={isLoadingBranches}
            hideOrgSummaryOnDesktop={hideOrgSummaryOnDesktop}
            compactLayout={compactPublicLayout}
            onMemberNoAvailabilityChange={handleMemberNoAvailabilityChange}
          />
        ),
      },
      {
        name: "payment",
        component: (
          <Payment
            amountBreakdown={break_down}
            onSelectionChange={handleSelectionChange}
            isPublicSignup={isPublicSignup}
          />
        ),
      },
    ],
    [
      imgFile,
      errorImg,
      generationError,
      isPublicSignup,
      branches,
      companyDetails,
      isLoadingBranches,
      hideOrgSummaryOnDesktop,
      compactPublicLayout,
      break_down,
      handleSelectionChange,
      handleMemberNoAvailabilityChange,
    ]
  );

  if (isGeneratingId || isLoadingFee) {
    return loadingComponent || <Loading />;
  }

  return (
    <>
      {isSubmitting && (
        <FormLoadingOverlay
          message="Creating Member..."
          subMessage="Please wait while we process your registration"
        />
      )}
      <Formik
        initialValues={formikValues}
        enableReinitialize={true}
        onSubmit={async (values, helpers) => {
          if (memberNoBlocksProceed) {
            toast.error(
              memberNoAvailability.isChecking
                ? "Please wait while we verify the membership number"
                : memberNoAvailability.message ||
                    "Please enter an available membership number"
            );
            return;
          }
          await onSubmit({
            ...values,
            imgFile,
            selectedBreakdown,
            selectedBreakdownWithAmounts,
            selectedAmount,
            setPageName,
            resetForm: helpers.resetForm,
            setImgFile,
            setErrorImg,
            refetchMemberId,
            branchId,
            amount,
            break_down,
          });
        }}
        validationSchema={validationSchema}
      >
        {({ isSubmitting: formikIsSubmitting }) => (
          <Form
            style={{
              position: "relative",
              opacity: isSubmitting ? 0.6 : 1,
              pointerEvents: isSubmitting ? "none" : "auto",
            }}
          >
            {showHeader && (
              <CardComp cardClassName={"border-0 ps-3 mb-3"}>
                <h3 className="mb-0 text-black1 fw-bold">{formTitle}</h3>
              </CardComp>
            )}
            {compactPublicLayout ? (
              <>
                {showStepper && (
                  <PublicSignupStepper
                    currentStep={pageName === "personal_details" ? 0 : 1}
                  />
                )}
                <PageTab PageArray={usePage} page={pageName} />
              </>
            ) : (
              <CardComp cardClassName={"border-0 ps-0 pe-0 pt-0"}>
                {showStepper && (
                  <div className="d-flex justify-content-center align-items-center mb-4 px-2">
                    <div className="col-12 col-md-10 col-lg-8 position-relative d-flex justify-content-center align-items-center">
                      <div className="pointer">
                        <img
                          src={
                            pageName !== "personal_details"
                              ? "/Icon/tick-circle-success.svg"
                              : "/Members/circle.svg"
                          }
                          alt="selected"
                        />
                        <p
                          style={{ left: "-34px" }}
                          className="text-center position-absolute h60"
                        >
                          Personal details
                        </p>
                      </div>
                      <div
                        style={{
                          padding: "0.5px 0",
                          backgroundColor: "#525252",
                        }}
                        className="w-100"
                      ></div>
                      <div className="pointer">
                        <img
                          src={
                            pageName === "personal_details"
                              ? "/Members/circle-none.svg"
                              : "/Members/circle.svg"
                          }
                          alt="selected"
                        />
                        <p
                          style={{ right: "-18px" }}
                          className="text-center h60 position-absolute"
                        >
                          Payment
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <PageTab PageArray={usePage} page={pageName} />
                </div>
              </CardComp>
            )}
            {!compactPublicLayout && <div className="col-12 py-3"></div>}
            {compactPublicLayout && <div className="pt-3"></div>}
            {pageName === "personal_details" ? (
              <div
                className={`d-flex ${
                  alignActionsEnd
                    ? "justify-content-end"
                    : "justify-content-start"
                }`}
              >
                <div className="col-12 col-sm-6 col-md-5 col-lg-4">
                  <FormikCheckBtn
                    formikFields={[
                      ...Object.keys(personal_details),
                      "member_no",
                      ...(isPublicSignup ? ["branch_id"] : []),
                    ]}
                    isDisabled={memberNoBlocksProceed}
                    onBtnClick={() => {
                      if (memberNoBlocksProceed) {
                        toast.error(
                          memberNoAvailability.isChecking
                            ? "Please wait while we verify the membership number"
                            : memberNoAvailability.message ||
                                "Please enter an available membership number"
                        );
                        return;
                      }
                      setPageName("payment");
                    }}
                  />
                </div>
              </div>
            ) : (
              <div
                className={`d-flex flex-column flex-sm-row gap-3 ${
                  alignActionsEnd
                    ? "justify-content-end"
                    : "justify-content-start"
                }`}
              >
                <div className="">
                  <ButtonComp
                    BorderColor={"var(--color-primary)"}
                    btnText={"Previous"}
                    onClick={() => {
                      setPageName("personal_details");
                    }}
                    btnClassName={`w-100 bg-white1 text-primary1 py-2 px-4 fw-semibold rounded ${
                      isSubmitting ? "opacity-50" : ""
                    }`}
                    type={"button"}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="">
                  <ButtonComp
                    btnText={
                      isSubmitting ? "Creating Member..." : submitBtnText
                    }
                    btnClassName={`w-100 bg-primary1 py-2 px-4 text-white fw-semibold border-0 rounded ${
                      isSubmitting ? "opacity-75" : ""
                    }`}
                    type={"submit"}
                    disabled={isSubmitting || memberNoBlocksProceed}
                    loading={isSubmitting}
                  />
                </div>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}
